import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./Knowledgebase.css";
import API_BASE_URL from "../config";
import FilterTabs from "./FilterTabs";
import DocumentEdition from "./DocumentEdition";
import { apiFetchJson } from "../apiClient";
import { useAuth } from "../AuthContext";
import useDocumentStatusStream from "../hooks/useDocumentStatusStream";
import useUploadConfig from "../hooks/useUploadConfig";
import DocumentTypeSelect, { appendDocumentType } from "./DocumentTypeSelect";
import { uploadAcceptAttribute, validateUploadFiles } from "../utils/uploadValidate";

// Processing state → design-system badge tone. States not listed (pending /
// unknown) use the neutral badge.
const STATUS_BADGE_TONE = {
  completed: "ds-badge--success",
  failed: "ds-badge--danger",
  processing: "ds-badge--info",
};

/** One row's live processing status: SSE + safety-net poll (feature 3). */
function DocStatusChip({ documentId, filename }) {
  const { status } = useDocumentStatusStream(documentId);
  const st = status && status.status;
  const label = st === "completed" ? "Completed"
    : st === "failed" ? "Failed"
      : st === "processing" ? "Processing…"
        : st === "pending" ? "Queued…"
          : status ? "" : "…";
  if (!label) return null;
  const tone = STATUS_BADGE_TONE[st] || "";
  return (
    <span className={`ds-badge ${tone} kb-live-status kb-live-status--${st || "pending"}`}>
      <span className="kb-live-status-name">{filename}</span>: {label}
    </span>
  );
}

function useOptionalUser() {
  try {
    return useAuth().user;
  } catch {
    return null;
  }
}

function DuplicateModal({ names, onUploadAnyway, onCancel }) {
  return (
    <div className="kb-duplicate-overlay">
      <div className="kb-duplicate-modal" role="dialog" aria-modal="true" aria-labelledby="kb-duplicate-title">
        <h3 id="kb-duplicate-title" className="ds-section-title">Files already exist</h3>
        <p>These files are already in the workspace. Upload anyway?</p>
        <ul className="kb-duplicate-file-list">
          {names.map((n) => (
            <li key={n}>📄 {n}</li>
          ))}
        </ul>
        <div className="kb-duplicate-modal-actions">
          <button className="ds-btn ds-btn--primary" onClick={onUploadAnyway}>
            Upload anyway
          </button>
          <button className="ds-btn ds-btn--secondary" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Knowledgebase({ database = "Betonbau" }) {
  const { t } = useTranslation();
  const user = useOptionalUser();
  const isAdmin = user?.role === "admin";
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadEdition, setUploadEdition] = useState("");
  const [collectionId, setCollectionId] = useState("");
  const [collections, setCollections] = useState([]);
  const [fileStatuses, setFileStatuses] = useState({});
  const uploadCfg = useUploadConfig(database);
  const [documentType, setDocumentType] = useState("");   // "" = workspace default
  const [mineOnly, setMineOnly] = useState(false);
  const [liveBatch, setLiveBatch] = useState([]);   // [{documentId, filename}] of the current batch
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Remove flow
  const [confirmingId, setConfirmingId] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [removeError, setRemoveError] = useState({});
  // Duplicate upload modal
  const [duplicateModal, setDuplicateModal] = useState(null);

  // -----------------------------
  // FETCH DOCUMENTS
  // -----------------------------
  const fetchDocuments = async () => {
    try {
      setLoading(true);

      const res = await fetch(
        `${API_BASE_URL}/api/v1/documents?database=${encodeURIComponent(database)}${mineOnly ? "&mine=true" : ""}`,
        {
          headers: new Headers({
            "ngrok-skip-browser-warning": "69420",
          }),
        },
      );

      if (!res.ok) throw new Error("Failed to fetch documents");

      const data = await res.json();
      setDocuments(data.documents || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setDocuments([]); // important when switching DB / owner filter
    setError(null);
    setConfirmingId(null);
    setRemoveError({});
    fetchDocuments();
  }, [database, mineOnly]);

  useEffect(() => {
    setDocumentType(""); // document types are per workspace
  }, [database]);

  useEffect(() => {
    if (!isAdmin) {
      setCollections([]);
      return undefined;
    }
    let stale = false;
    apiFetchJson(`/api/v1/collections?database=${encodeURIComponent(database)}`)
      .then((list) => {
        if (!stale) setCollections(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!stale) setCollections([]);
      });
    return () => {
      stale = true;
    };
  }, [isAdmin, database]);

  // -----------------------------
  // REMOVE DOCUMENT
  // -----------------------------
  const handleRemove = async (docId) => {
    setRemoving(docId);
    setRemoveError((prev) => { const next = { ...prev }; delete next[docId]; return next; });
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/v1/documents/${encodeURIComponent(docId)}?database=${encodeURIComponent(database)}`,
        {
          method: "DELETE",
          headers: new Headers({ "ngrok-skip-browser-warning": "69420" }),
        },
      );
      if (!res.ok && res.status !== 404) {
        let detail = "Remove failed";
        try { detail = (await res.json()).detail || detail; } catch (_) {}
        throw new Error(detail);
      }
      // 200 or 404 — treat both as success (document is gone)
      setDocuments((prev) => prev.filter((d) => d.document_id !== docId));
      setConfirmingId((prev) => (prev === docId ? null : prev));
    } catch (err) {
      setRemoveError((prev) => ({ ...prev, [docId]: err.message }));
    } finally {
      setRemoving(null);
    }
  };

  // -----------------------------
  // UPLOAD HELPER (shared by new-file and "upload anyway")
  // -----------------------------
  const uploadFiles = async (files, edition = "", batchCollection = "", batchType = "") => {
    setUploading(true);
    const pending = {};
    files.forEach((f) => (pending[f.name] = "Uploading..."));
    setFileStatuses((prev) => ({ ...prev, ...pending }));
    try {
      const formData = new FormData();
      files.forEach((f) => formData.append("files", f));
      if (edition) formData.append("edition", edition);
      if (batchCollection && isAdmin) formData.append("collection_id", batchCollection);
      appendDocumentType(formData, batchType);
      const response = await fetch(
        `${API_BASE_URL}/api/v1/documents/upload?database=${encodeURIComponent(database)}`,
        {
          method: "POST",
          body: formData,
          headers: new Headers({ "ngrok-skip-browser-warning": "69420" }),
        },
      );
      if (!response.ok) {
        let detail = "Upload failed";
        try { detail = (await response.json()).detail || detail; } catch (_) {}
        throw new Error(detail);
      }
      const results = await response.json();
      const statuses = {};
      results.forEach((doc) => { statuses[doc.filename] = doc.message || "Uploaded"; });
      setFileStatuses((prev) => ({ ...prev, ...statuses }));
      setLiveBatch(results
        .filter((doc) => doc.document_id)
        .map((doc) => ({ documentId: doc.document_id, filename: doc.filename })));
      fetchDocuments();
    } catch (err) {
      const errStatuses = {};
      files.forEach((f) => { errStatuses[f.name] = err.message || "Upload failed"; });
      setFileStatuses((prev) => ({ ...prev, ...errStatuses }));
    } finally {
      setUploading(false);
    }
  };

  // -----------------------------
  // AUTO UPLOAD ON FILE SELECT
  // -----------------------------
  const handleFileSelect = async (e) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length === 0) return;

    const { valid, errors } = validateUploadFiles(selected, uploadCfg);

    setFileStatuses(errors);
    if (valid.length === 0) return;

    // Snapshot the edition for THIS batch (the duplicate modal re-uses it) and
    // clear the field so it can never leak onto a later, unrelated batch.
    const batchEdition = uploadEdition.trim();
    const batchCollection = collectionId;
    const batchType = documentType;
    setUploadEdition("");
    setCollectionId("");
    setUploading(true);

    try {
      // Check each valid file against Neo4j in parallel
      const checks = await Promise.all(
        valid.map((f) =>
          fetch(
            `${API_BASE_URL}/api/v1/documents/check?filename=${encodeURIComponent(f.name)}&database=${encodeURIComponent(database)}`,
            { headers: new Headers({ "ngrok-skip-browser-warning": "69420" }) },
          )
            .then((r) => r.json())
            .catch(() => ({ exists: false })), // network error → treat as new (fail open)
        ),
      );

      const newFiles = [];
      const dupFiles = [];
      valid.forEach((f, i) => {
        if (checks[i].exists) dupFiles.push(f);
        else newFiles.push(f);
      });

      // Upload non-duplicates immediately
      if (newFiles.length > 0) await uploadFiles(newFiles, batchEdition, batchCollection, batchType);

      // Ask about duplicates via modal
      if (dupFiles.length > 0) {
        setDuplicateModal({
          files: dupFiles,
          names: dupFiles.map((f) => f.name),
          edition: batchEdition,
          collectionId: batchCollection,
          documentType: batchType,
        });
      }
    } finally {
      // allow re-selecting same file next time, always reset uploading flag
      e.target.value = "";
      setUploading(false);
    }
  };

  // -----------------------------
  // HELPERS
  // -----------------------------
  const buildFileUrl = (path) => {
    if (!path) return "#";
    return path.startsWith("http")
      ? path
      : `${API_BASE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
  };

  const formatDate = (value) =>
    value ? new Date(value).toLocaleString() : "—";

  const statusEntries = Object.entries(fileStatuses);

  // -----------------------------
  // UI
  // -----------------------------
  return (
    <div className="ds-page kb-page">
      {/* HEADER */}
      <header className="ds-page-header kb-header">
        <div className="kb-header-text">
          <p className="ds-page-subtitle">
            Upload documents to this workspace and manage their editions.
          </p>
        </div>
        <div className="kb-header-actions">
          <span className="kb-doc-count">
            {documents.length} document{documents.length === 1 ? "" : "s"}
          </span>
          <button
            type="button"
            className="ds-btn ds-btn--secondary ds-btn--sm"
            onClick={() => setMineOnly((v) => !v)}
          >
            {mineOnly ? "All documents" : "My uploads"}
          </button>
        </div>
      </header>

      {/* UPLOAD */}
      <section className="ds-card kb-upload-card">
        <div className="ds-form-row">
          {/* OPTIONAL EDITION (must be filled before selecting files) */}
          <div className="ds-field kb-field kb-field--wide">
            <label htmlFor="kb-upload-edition">Edition (optional)</label>
            <input
              id="kb-upload-edition"
              className="ds-input"
              type="text"
              value={uploadEdition}
              onChange={(e) => setUploadEdition(e.target.value)}
              disabled={uploading}
              placeholder="e.g. DIN EN 1992-1-1:2011-01"
              dir="auto"
            />
            <small className="ds-help">
              Applies to the files you select next (uploads start immediately).
              Edit individual editions in the list below afterwards.
            </small>
          </div>

          <DocumentTypeSelect
            id="kb-upload-document-type"
            className="ds-field kb-field"
            types={uploadCfg?.document_types}
            value={documentType}
            onChange={setDocumentType}
            disabled={uploading}
          />

          {isAdmin && (
            <div className="ds-field kb-field">
              <label htmlFor="kb-upload-collection">
                {t("upload.collectionLabel")}
              </label>
              <select
                id="kb-upload-collection"
                className="ds-select"
                value={collectionId}
                onChange={(e) => setCollectionId(e.target.value)}
                disabled={uploading}
              >
                <option value="">{t("upload.collectionNone")}</option>
                {collections.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* UPLOAD AREA (AUTO UPLOAD) */}
        <label className={`kb-upload-area${uploading ? " kb-upload-area--busy" : ""}`}>
          <input
            type="file"
            multiple
            accept={uploadAcceptAttribute(uploadCfg)}
            onChange={handleFileSelect}
            disabled={uploading}
            hidden
          />

          <div className="kb-upload-inner">
            <div className="kb-upload-icon" aria-hidden="true">📥</div>

            <div className="kb-upload-title">
              {uploading ? "Uploading..." : "Drop documents to ingest"}
            </div>

            <div className="kb-upload-subtext">
              Z-17 approvals · LV documents · Product catalogs · Eurocodes ·
              Prüfberichte
            </div>
          </div>
        </label>

        {/* STATUS */}
        {statusEntries.length > 0 && (
          <ul className="kb-file-status">
            {statusEntries.map(([name, msg]) => (
              <li key={name}>
                <span className="kb-file-status-name">{name}</span>
                <span className="kb-file-status-msg">{msg}</span>
              </li>
            ))}
          </ul>
        )}

        {liveBatch.length > 0 && (
          <div className="kb-live-status-list">
            {liveBatch.map((entry) => (
              <DocStatusChip key={entry.documentId} documentId={entry.documentId} filename={entry.filename} />
            ))}
          </div>
        )}
      </section>

      {/* DOCUMENT LIST */}
      <section className="kb-documents">
        <FilterTabs />

        {loading && documents.length === 0 && (
          <div className="ds-empty">Loading...</div>
        )}

        {error && <div className="ds-alert ds-alert--danger">⚠️ {error}</div>}

        {!loading && documents.length === 0 ? (
          <div className="ds-empty">No documents uploaded yet</div>
        ) : (
          <div className="kb-doc-list">
            {documents.map((doc) => (
              <div key={doc.stored_filename}>
                {confirmingId === doc.document_id ? (
                  <div className="kb-doc-confirm" role="group" aria-label={`Remove ${doc.filename}`}>
                    <span className="kb-doc-name" title={doc.filename}>📄 {doc.filename}</span>
                    <div className="kb-doc-confirm-question">
                      ⚠ Remove this document and all its graph nodes &amp; edges?
                    </div>
                    <div className="kb-doc-actions">
                      <button
                        className="ds-btn ds-btn--danger ds-btn--sm"
                        disabled={removing === doc.document_id}
                        onClick={() => handleRemove(doc.document_id)}
                      >
                        {removing === doc.document_id ? "Removing…" : "Yes, remove"}
                      </button>
                      <button
                        className="ds-btn ds-btn--secondary ds-btn--sm"
                        disabled={removing === doc.document_id}
                        onClick={() => { setConfirmingId(null); setRemoveError((p) => { const n = { ...p }; delete n[doc.document_id]; return n; }); }}
                      >
                        Cancel
                      </button>
                    </div>
                    {removeError[doc.document_id] && (
                      <div className="kb-doc-confirm-error" role="alert">⚠ {removeError[doc.document_id]}</div>
                    )}
                  </div>
                ) : (
                  <div className="kb-doc-row">
                    <div className="kb-doc-content">
                      <span className="kb-doc-name" title={doc.filename}>📄 {doc.filename}</span>
                      <div className="kb-doc-meta">
                        <span className="kb-doc-date">{formatDate(doc.uploaded_at)}</span>
                        <DocumentEdition doc={doc} database={database} onChanged={fetchDocuments} />
                      </div>
                    </div>
                    <div className="kb-doc-actions">
                      <a
                        className="ds-btn ds-btn--secondary ds-btn--sm"
                        href={buildFileUrl(doc.url)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <span aria-hidden="true">⬇</span> Download
                      </a>
                      <button
                        className="ds-btn ds-btn--danger ds-btn--sm"
                        onClick={() => setConfirmingId(doc.document_id)}
                      >
                        <span aria-hidden="true">✕</span> Remove
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {duplicateModal && (
        <DuplicateModal
          names={duplicateModal.names}
          onUploadAnyway={async () => {
            const {
              files: filesToUpload,
              edition: batchEdition,
              collectionId: batchCollection,
              documentType: batchType,
            } = duplicateModal;
            setDuplicateModal(null);
            await uploadFiles(filesToUpload, batchEdition, batchCollection, batchType);
          }}
          onCancel={() => {
            const names = duplicateModal.names;
            setDuplicateModal(null);
            setFileStatuses((prev) => {
              const next = { ...prev };
              names.forEach((n) => delete next[n]);
              return next;
            });
          }}
        />
      )}
    </div>
  );
}
